import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Search, CheckSquare, Layers, BookOpen, ArrowRight, Plus, Mic,
    Clock, Folder, Timer, Hash, Zap, Settings, Sparkles, Gamepad2
} from 'lucide-react';
import IconRenderer from './ui/IconRenderer';
import useProjectStore from '../stores/useProjectStore';
import useAreaStore from '../stores/useAreaStore';
import useResourceStore from '../stores/useResourceStore';
import useTaskStore from '../stores/useTaskStore';
import useSessionStore from '../stores/useSessionStore';
import useVoiceDumpStore from '../stores/useVoiceDumpStore';
import useUserStore from '../stores/useUserStore';
import { storage } from '../utils/storage';
import './CommandPalette.css';

const MAX_PER_GROUP = 5;
const MAX_RECENT = 5;

/**
 * Fuzzy-ish scoring: returns 0 (no match) or positive score.
 * Higher = better match. Rewards prefix matches and word-boundary hits.
 */
function scoreMatch(text, query) {
    if (!query) return 1;
    const t = text.toLowerCase();
    const q = query.toLowerCase();

    // Exact prefix match → highest score
    if (t.startsWith(q)) return 100;
    // Contains → good score
    if (t.includes(q)) return 50;
    // Word boundary match (check if query matches the start of any word)
    const words = t.split(/[\s\-_/]+/);
    for (const w of words) {
        if (w.startsWith(q)) return 70;
    }
    return 0;
}

const CATEGORY_CONFIG = {
    action:   { label: 'Actions',   icon: Zap,       order: 0 },
    project:  { label: 'Projects',  icon: Layers,    order: 1 },
    task:     { label: 'Tasks',     icon: CheckSquare, order: 2 },
    resource: { label: 'Resources', icon: BookOpen,  order: 3 },
    session:  { label: 'Logs',      icon: Timer,     order: 4 },
    page:     { label: 'Pages',     icon: ArrowRight, order: 5 },
};

export default function CommandPalette() {
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);

    const projects = useProjectStore(s => s.projects);
    const areas = useAreaStore(s => s.areas);
    const resources = useResourceStore(s => s.resources);
    const tasks = useTaskStore(s => s.tasks);
    const sessions = useSessionStore(s => s.sessions);
    const openVoiceDump = useVoiceDumpStore(s => s.openWizard);
    const isModuleEnabled = useUserStore(s => s.isModuleEnabled);

    // Recent searches
    const [recentSearches, setRecentSearches] = useState(() =>
        storage.get('command_palette_recent', [])
    );

    // Toggle with Cmd+K
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k' && !e.shiftKey) {
                e.preventDefault();
                setIsOpen(prev => !prev);
            }
            if (e.key === 'Escape') {
                setIsOpen(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    // Reset on open/query
    useEffect(() => {
        setSelectedIndex(0);
    }, [query, isOpen]);

    // Build all searchable items grouped
    const grouped = useMemo(() => {
        if (!isOpen) return [];
        const q = query.trim();

        // ── Build items ──
        const allItems = [];

        // Actions
        const actions = [
            { type: 'action', id: 'new-task', label: 'Create New Task', subtitle: 'Cmd+Shift+K', icon: Plus, path: '/inbox' },
            { type: 'action', id: 'new-note', label: 'Create New Resource', subtitle: 'Note, link, or file', icon: Plus, path: '/resources' },
            { type: 'action', id: 'voice-dump', label: 'Voice Dump', subtitle: 'Capture thoughts by voice', icon: Mic, action: 'voice-dump' },
            { type: 'action', id: 'new-project', label: 'Create New Project', subtitle: 'Start a new project', icon: Plus, path: '/projects' },
        ];
        actions.forEach(a => allItems.push(a));

        // Pages — core always-on; gated pages carry a `module` and are filtered by isModuleEnabled
        const pages = [
            { type: 'page', id: 'p-home', label: 'Home', icon: Sparkles, path: '/' },
            { type: 'page', id: 'p-today', label: 'Today', icon: ArrowRight, path: '/today' },
            { type: 'page', id: 'p-quick-capture', label: 'Quick Capture', icon: ArrowRight, path: '/quick-capture' },
            { type: 'page', id: 'p-focus', label: 'Focus Timer', icon: Timer, path: '/focus' },
            { type: 'page', id: 'p-circuits', label: 'Circuits', icon: ArrowRight, path: '/circuits' },
            { type: 'page', id: 'p-rituals', label: 'Rituals', icon: ArrowRight, path: '/rituals', module: 'rituals' },
            { type: 'page', id: 'p-dashboard', label: 'Dashboard', icon: ArrowRight, path: '/dashboard', module: 'dashboard' },
            { type: 'page', id: 'p-projects', label: 'Projects', icon: Layers, path: '/projects', module: 'projects' },
            { type: 'page', id: 'p-tasks', label: 'Tasks', icon: CheckSquare, path: '/tasks' },
            { type: 'page', id: 'p-logs', label: 'Logs', icon: ArrowRight, path: '/sessions' },
            { type: 'page', id: 'p-areas', label: 'Areas', icon: Hash, path: '/areas', module: 'areas' },
            { type: 'page', id: 'p-resources', label: 'Resources', icon: BookOpen, path: '/resources', module: 'resources' },
            { type: 'page', id: 'p-weekly-review', label: 'Weekly Review', icon: ArrowRight, path: '/weekly-review', module: 'reviews' },
            { type: 'page', id: 'p-quarterly-review', label: 'Quarterly Review', icon: ArrowRight, path: '/quarterly-review', module: 'reviews' },
            // Ambient core, never gated — same as Settings below. Previously
            // absent here entirely: the full AI Coach page (history, search,
            // BYOK settings) and Gamification had no command-palette entry,
            // one of the surfaces named in the reachability audit that found
            // them unlinked from everywhere else too (see Sidebar.jsx).
            { type: 'page', id: 'p-gamification', label: 'Gamification', icon: Gamepad2, path: '/gamification' },
            { type: 'page', id: 'p-ai-coach', label: 'AI Coach', icon: Sparkles, path: '/' },
            { type: 'page', id: 'p-settings', label: 'Settings', icon: Settings, path: '/settings' },
        ];
        pages
            .filter(p => !p.module || isModuleEnabled(p.module))
            .forEach(p => allItems.push(p));

        // Projects
        projects.forEach(p => {
            const area = areas.find(a => a.id === p.areaId);
            const taskCount = tasks.filter(t => t.projectId === p.id).length;
            const doneCount = tasks.filter(t => t.projectId === p.id && t.status === 'done').length;
            const pct = taskCount > 0 ? Math.round((doneCount / taskCount) * 100) : 0;
            allItems.push({
                type: 'project', id: p.id,
                label: p.name,
                subtitle: `${p.status} · ${taskCount} tasks · ${pct}%${area ? ` · ${area.name}` : ''}`,
                icon: Layers,
                path: `/projects/${p.id}`,
                entityIcon: p.icon || p.emoji,
                status: p.status,
            });
        });

        // Tasks (active only)
        tasks.filter(t => t.status !== 'done' && t.status !== 'cancelled').forEach(t => {
            const project = projects.find(p => p.id === t.projectId);
            allItems.push({
                type: 'task', id: t.id,
                label: t.title,
                subtitle: `${t.status}${project ? ` · ${project.name}` : ''}${t.priority ? ` · ${t.priority}` : ''}`,
                icon: CheckSquare,
                path: t.projectId ? `/projects/${t.projectId}` : '/inbox',
                status: t.status,
            });
        });

        // Resources
        resources.filter(r => r.status === 'active').forEach(r => {
            allItems.push({
                type: 'resource', id: r.id,
                label: r.title,
                subtitle: r.type || 'resource',
                icon: BookOpen,
                path: '/resources',
            });
        });

        // Sessions (last 50, most recent first)
        const sortedSessions = [...sessions]
            .sort((a, b) => new Date(b.startTime) - new Date(a.startTime))
            .slice(0, 50);

        sortedSessions.forEach(s => {
            const project = projects.find(p => p.id === s.projectId);
            const date = new Date(s.startTime);
            const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            const mins = Math.round((s.actualMinutes || s.duration || 0));
            allItems.push({
                type: 'session', id: s.id,
                label: s.intention || s.title || `${dateStr} Session`,
                subtitle: `${dateStr} · ${mins}m${project ? ` · ${project.name}` : ''}`,
                icon: Timer,
                path: '/sessions',
            });
        });

        // ── Score & filter ──
        let scored;
        if (q) {
            scored = allItems
                .map(item => ({ ...item, _score: scoreMatch(item.label + ' ' + (item.subtitle || ''), q) }))
                .filter(item => item._score > 0)
                .sort((a, b) => b._score - a._score);
        } else {
            // No query: show actions first, then pages — skip data items to avoid clutter
            scored = allItems.filter(i => i.type === 'action' || i.type === 'page');
        }

        // ── Group by type ──
        const groups = {};
        scored.forEach(item => {
            if (!groups[item.type]) groups[item.type] = [];
            if (groups[item.type].length < MAX_PER_GROUP) {
                groups[item.type].push(item);
            }
        });

        // Order groups by CATEGORY_CONFIG
        const orderedGroups = Object.entries(groups)
            .sort(([a], [b]) => (CATEGORY_CONFIG[a]?.order ?? 99) - (CATEGORY_CONFIG[b]?.order ?? 99))
            .map(([type, items]) => ({
                type,
                label: CATEGORY_CONFIG[type]?.label || type,
                icon: CATEGORY_CONFIG[type]?.icon || ArrowRight,
                items,
            }));

        return orderedGroups;
    }, [isOpen, query, projects, areas, resources, tasks, sessions, isModuleEnabled]);

    // Flatten for keyboard navigation
    const flatResults = useMemo(() => {
        return grouped.flatMap(g => g.items);
    }, [grouped]);

    const saveRecentSearch = useCallback((q) => {
        if (!q.trim()) return;
        const next = [q.trim(), ...recentSearches.filter(r => r !== q.trim())].slice(0, MAX_RECENT);
        setRecentSearches(next);
        storage.set('command_palette_recent', next);
    }, [recentSearches]);

    const handleSelect = useCallback((item) => {
        if (!item) return;
        if (query.trim()) saveRecentSearch(query);
        if (item.action === 'voice-dump') {
            openVoiceDump();
        } else if (item.recentQuery) {
            setQuery(item.recentQuery);
            return;
        } else {
            navigate(item.path);
        }
        setIsOpen(false);
        setQuery('');
    }, [query, navigate, openVoiceDump, saveRecentSearch]);

    const handleKeyDown = (e) => {
        if (!isOpen) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(prev => Math.min(prev + 1, flatResults.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(prev => Math.max(prev - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            handleSelect(flatResults[selectedIndex]);
        }
    };

    if (!isOpen) return null;

    // Track flat index across groups
    let flatIdx = 0;

    return (
        <div className="command-palette-overlay" onClick={() => setIsOpen(false)}>
            <div className="command-palette" onClick={e => e.stopPropagation()}>
                <div className="command-palette__search">
                    <Search size={20} className="command-palette__icon" />
                    <input
                        autoFocus
                        type="text"
                        placeholder="Search projects, tasks, sessions..."
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        onKeyDown={handleKeyDown}
                        className="command-palette__input"
                    />
                    <kbd className="command-palette__shortcut">ESC</kbd>
                </div>

                <div className="command-palette__results">
                    {/* Recent searches when empty */}
                    {!query && recentSearches.length > 0 && (
                        <div className="cp-group">
                            <div className="cp-group__header">
                                <Clock size={12} />
                                <span>Recent</span>
                            </div>
                            {recentSearches.map((r, i) => (
                                <div
                                    key={`recent-${i}`}
                                    className="command-palette__item"
                                    onClick={() => setQuery(r)}
                                >
                                    <div className="command-palette__item-icon"><Clock size={14} /></div>
                                    <span className="command-palette__item-label">{r}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Grouped results */}
                    {grouped.length === 0 && query ? (
                        <div className="command-palette__empty">No results for "{query}"</div>
                    ) : (
                        grouped.map(group => {
                            const GroupIcon = group.icon;
                            return (
                                <div key={group.type} className="cp-group">
                                    <div className="cp-group__header">
                                        <GroupIcon size={12} />
                                        <span>{group.label}</span>
                                        <span className="cp-group__count">{group.items.length}</span>
                                    </div>
                                    {group.items.map(item => {
                                        const idx = flatIdx++;
                                        const Icon = item.icon;
                                        return (
                                            <div
                                                key={item.id}
                                                className={`command-palette__item ${idx === selectedIndex ? 'command-palette__item--selected' : ''}`}
                                                onClick={() => handleSelect(item)}
                                                onMouseEnter={() => setSelectedIndex(idx)}
                                            >
                                                <div className="command-palette__item-icon">
                                                    {item.entityIcon ? <IconRenderer icon={item.entityIcon} size={16} /> : <Icon size={16} />}
                                                </div>
                                                <div className="command-palette__item-text">
                                                    <span className="command-palette__item-label">{item.label}</span>
                                                    {item.subtitle && (
                                                        <span className="command-palette__item-sub">{item.subtitle}</span>
                                                    )}
                                                </div>
                                                <span className="command-palette__item-type">{item.type}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })
                    )}
                </div>

                <div className="command-palette__footer">
                    <span><kbd>↑</kbd> <kbd>↓</kbd> navigate</span>
                    <span><kbd>↵</kbd> select</span>
                    <span><kbd>Ctrl+K</kbd> toggle</span>
                </div>
            </div>
        </div>
    );
}
